import { Button, Typography, makeStyles } from "@material-ui/core";
import React, { useState } from "react";
import { useTranslate } from "react-admin";

import { useCurrentTerminal } from "../hooks/useCurrentTerminal";
import type { Place, StorageEntityInfo } from "../types/warehouse.types";
import { AssignCompanyProduct } from "./AssignCompanyProduct";
import { BuildingEntityBlockUnBlock } from "./BuildingEntityBlockUnBlock";
import { InfoBox } from "./InfoBox";
import { MultiplePalletsInfo } from "./MultiplePalletsInfo";
import { PlaceForSplittedSetClear } from "./PlaceForSplittedSetClear";
import { PlaceInformationContent } from "./PlaceInformationContent";
import { SinglePalletInfo } from "./SinglePalletInfo";

const useStyles = makeStyles((theme) => ({
  infoBox: {
    margin: "15px 0",
    maxHeight: "400px",
    overflowY: "auto",
  },
  placeHeader: {
    margin: 0,
    fontWeight: "bold",
    marginBottom: "5px",
    color: theme.palette.primary.main,
  },
  placeContainer: {
    marginBottom: "15px",
    paddingBottom: "10px",
    borderBottom: `1px solid ${theme.palette.storageUi.backgroundSecondary}`,
    "&:last-child": {
      marginBottom: 0,
      paddingBottom: 0,
      borderBottom: "none",
    },
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
  },
  buttonContainer: {
    marginTop: "10px",
    marginBottom: "10px",
  },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    flexBasis: "100%",
  },
}));

interface PlaceInfoProps {
  selectedPlaces: Place[];
  terminalId: number;
  storageEntities: StorageEntityInfo[];
  hideAssign?: boolean;
}

interface PlaceItemProps {
  place: Place;
  index: number;
  total: number;
  classes: ReturnType<typeof useStyles>;
}

const PlaceItem: React.FC<PlaceItemProps> = ({ place, index, total, classes }) => {
  const translate = useTranslate();
  return (
    <div className={classes.placeContainer}>
      {total > 1 && (
        <Typography component="p" variant="subtitle2" className={classes.placeHeader}>
          {translate('osfc.storage_ui.place_info.place_x_of_y', { index: index + 1, total })}
        </Typography>
      )}
      <PlaceInformationContent place={place} />
    </div>
  );
};

export const PlaceInfo: React.FC<PlaceInfoProps> = ({
  selectedPlaces,
  terminalId,
  storageEntities,
  hideAssign = false,
}) => {
  const classes = useStyles();
  const translate = useTranslate();
  const terminal = useCurrentTerminal();
  const [showAllPlaces, setShowAllPlaces] = useState(false);

  const firstThreePlaces = selectedPlaces.slice(0, 3);
  const remainingPlaces = selectedPlaces.slice(3);
  const totalPlaces = selectedPlaces.length;

  // Assign and block/unblock are not applicable to virtual places (missing/additional)
  const physicalPlaces = selectedPlaces.filter((place) => !place.isVirtual);
  const areSplittedPalletsProcessed = Boolean(terminal?.should_process_splitted_pallets);

  if (selectedPlaces.length === 0) return null;

  return (
    <>
      {!hideAssign && physicalPlaces.length > 0 && (
        <AssignCompanyProduct selectedPlaces={physicalPlaces} />
      )}

      {/* Pallet Information */}
      {storageEntities.length > 0 && (
        <InfoBox title={translate('osfc.storage_ui.pallet_info.title')} style={{ margin: "15px 0", maxHeight: "400px", overflowY: "auto" }}>
          {storageEntities.length === 1 ? (
            <SinglePalletInfo storageEntity={storageEntities[0]} showTitle={false} showExtendedInfo={false} />
          ) : (
            <MultiplePalletsInfo storageEntities={storageEntities} />
          )}
        </InfoBox>
      )}

      {/* Selected Places Information */}
      <InfoBox
        title={
          <>
            {totalPlaces === 1 ? translate('osfc.storage_ui.place_info.selected_place') : translate('osfc.storage_ui.place_info.selected_places')}
            {physicalPlaces.length > 0 && (
              <div className={classes.actions}>
                <BuildingEntityBlockUnBlock
                  selectedEntities={physicalPlaces}
                  terminalId={terminalId}
                  entityType="place"
                  resource="areas"
                  idField="id"
                  displayField="address"
                  layout="stacked"
                />
                {areSplittedPalletsProcessed && (
                  <PlaceForSplittedSetClear selectedPlaces={physicalPlaces} terminalId={terminalId} />
                )}
              </div>
            )}
          </>
        }
        style={{ margin: "15px 0", maxHeight: "400px", overflowY: "auto" }}
      >
        {firstThreePlaces.map((place, index) => (
          <PlaceItem
            key={place.address}
            place={place}
            index={index}
            total={totalPlaces}
            classes={classes}
          />
        ))}

        {totalPlaces > 3 && (
          <div className={classes.buttonContainer}>
            <Button
              onClick={() => setShowAllPlaces(!showAllPlaces)}
              variant="outlined"
              color="secondary"
              size="small"
              fullWidth
            >
              {showAllPlaces ? translate('osfc.storage_ui.place_info.show_less') : translate('osfc.storage_ui.place_info.show_all', { count: totalPlaces })}
            </Button>
          </div>
        )}

        {showAllPlaces &&
          remainingPlaces.map((place, index) => (
            <PlaceItem
              key={place.address}
              place={place}
              index={index + 3}
              total={totalPlaces}
              classes={classes}
            />
          ))}
      </InfoBox>
    </>
  );
};
